import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FlatList, Keyboard, Platform, ScrollView, View} from 'react-native';
import Animated, {SlideInDown, SlideOutDown, SlideOutRight} from 'react-native-reanimated';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';
import {useDebounce} from 'use-debounce';

import BarcodeScanner from '../../components/BarcodeScanner';
import GiftCardTileAbbreviatedHorizontal from '../../components/GiftCardTileAbbreviatedHorizontal';
import {BarcodeIconButton, CircleXMarkIconButton, SearchIconButton} from '../../components/icons';
import {ProductTileHorizontal} from '../../components/ProductTile';
import PromotionSearchAbbreviatedTile from '../../components/PromotionSearchAbbreviatedTile';
import SearchBrandAbbreviatedTile from '../../components/SearchBrandAbbreviatedTile';
import SearchDiscoverAbbreviatedTile from '../../components/SearchDiscoverAbbreviatedTile';
import {SearchItemSkeleton} from '../../components/skeleton';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {useScrollCollapseReset} from '../../hooks/useScrollCollapse';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {usePromotions} from '../../queries/promotions';
import {useGlobalSearchLines} from '../../queries/search/useGlobalSearchLines';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {logger, serializeError} from '../../utils/logger';
import {applyTierSort} from '../../utils/rewards';
import {filterSearchText} from '../../utils/string';

import styles from './styles';

const GIFT_CARD_RESULT_TYPE = 'gift-card';

const FullScreenMessageView = ({children}: {children: ReactNode}) => (
  <View
    style={{
      justifyContent: 'flex-end',
      alignItems: 'center',
      height: 200,
    }}>
    <Text
      style={{
        fontSize: 16,
        fontWeight: '500',
        color: theme.colour.text.heading.primary,
      }}>
      {children}
    </Text>
  </View>
);

// PERF: list-performance-inline-objects — hoisted sub-components
const SEARCH_ITEM_SEPARATOR_STYLE = {marginTop: 8};
const SearchItemSeparator = () => <View style={SEARCH_ITEM_SEPARATOR_STYLE} />;
const FOOTER_STYLE = {marginVertical: 24};

type SearchBarRightIconsProps = {
  onClearSearchPress: () => void;
  onScanBarcodePress: () => void;
  isCircleXMarkIconButtonVisible: boolean;
  isBarcodeIconButtonVisible: boolean;
};
const SearchBarRightIcons = ({
  onClearSearchPress,
  onScanBarcodePress,
  isCircleXMarkIconButtonVisible,
  isBarcodeIconButtonVisible,
}: SearchBarRightIconsProps) => (
  <View style={{flexDirection: 'row', marginLeft: 4}}>
    {!!isCircleXMarkIconButtonVisible && <CircleXMarkIconButton onPress={onClearSearchPress} />}
    {!!isBarcodeIconButtonVisible && Platform.OS !== 'web' && (
      <BarcodeIconButton onPress={onScanBarcodePress} />
    )}
  </View>
);

type SearchBarProps = {
  editable: boolean;
  onManualSearchPress: () => void;
  onScanBarcodePress: () => void;
  onClearSearchPress: () => void;
  onSubmitEditing: ({nativeEvent}: {nativeEvent: {text: string}}) => void;
  onTextChanged: (text: string) => void;
  value: string;
};

const SearchBar = ({
  editable,
  onManualSearchPress,
  onScanBarcodePress,
  onClearSearchPress,
  onSubmitEditing,
  onTextChanged,
  value,
}: SearchBarProps) => {
  const inputRef = useRef(null);

  useFocusEffect(
    React.useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // This hack is needed because autofocus doesn't work properly
        // on web, giving inconsistent behaviour between first mount and otherwise
        setTimeout(() => {
          inputRef.current.focus();
        }, 1);
      }
      return () => null;
    }, []),
  );

  return (
    <View style={styles.searchBarHolder}>
      <View style={styles.searchBar}>
        <TextInput
          ref={inputRef}
          autoCorrect={false}
          autoFocus
          webMinFontSize
          dataDetectorTypes="none"
          editable={editable}
          leftIcon={<SearchIconButton onPress={onManualSearchPress} />}
          maxLength={80}
          onChangeText={onTextChanged}
          onSubmitEditing={onSubmitEditing}
          placeholder="What are you looking for?"
          placeholderTextColor="lightgrey"
          returnKeyType="search"
          rightIcon={
            <SearchBarRightIcons
              isBarcodeIconButtonVisible={!value}
              isCircleXMarkIconButtonVisible={!!value}
              onClearSearchPress={onClearSearchPress}
              onScanBarcodePress={onScanBarcodePress}
            />
          }
          spellCheck={false}
          style={styles.textInput}
          testID="search-input"
          underlineColorAndroid="transparent"
          value={value}
        />
      </View>
    </View>
  );
};

const SearchScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.NEW_SEARCH>) => {
    const {globalStore, brandsStore, feedStore, shopSelectionStore} = useStores();
    const {promotions} = usePromotions();
    // No scroll handler of its own: without this reset ctx.scrollY keeps the previous
    // screen's value, so the scroll-to-top button shows over the empty entry (QA 2026-10-21).
    useScrollCollapseReset();

    // ── Local search state ──────────────────────────────────────────────
    // SearchScreen manages its own query state entirely via local useState
    // and useDebounce.  It does NOT read from or write to SearchContext
    // (which is owned by the shop/department browsing flow).  This eliminates
    // all dispatch race conditions between the two flows.

    // Initialize with forwarded keywords so useDebounce starts with the
    // correct value on mount (avoids a 300ms gap before the first query).
    const [searchText, setSearchText] = useState(route.params?.keywords || '');
    const [selectedSearchResult, setSelectedSearchResult] = useState(null);
    // Secondary search results
    const [brand, setBrand] = useState(null);
    const [activations, setActivations] = useState([]);
    const [discoverArticles, setDiscoverArticles] = useState([]);
    // Barcode scan
    const [scanning, setScanning] = useState(false);
    const [scannedBarcodeValue, setScannedBarcodeValue] = useState('');
    const [isBarcodeSearching, setIsBarcodeSearching] = useState(false);
    const [isBarcode, setIsBarcode] = useState(false);

    // ── Forwarded keywords (from StickyFilterBar "search all") ─────────
    // Read before query setup so activeKeywords can use it synchronously.
    const forwardedKeywords = route.params?.keywords;

    // ── Query ───────────────────────────────────────────────────────────
    // storeId comes directly from MobX (observer() makes this reactive).
    // debouncedSearchText drives the TanStack query via useGlobalSearchLines
    // — no SearchContext middleman, no shared state, no dispatch races.
    //
    // activeKeywords handles the "already mounted" edge case: useState()
    // only initializes on first mount.  When React Navigation re-focuses
    // an already-mounted SearchScreen with new keywords, searchText is
    // still '' from the previous visit.  forwardedKeywords is available
    // immediately (from route.params) — use it as a synchronous override
    // so the query fires on THIS render, not 300ms later after the
    // debounce settles.
    const storeId = shopSelectionStore.selectedStore?.id ?? null;
    const [debouncedSearchText] = useDebounce(searchText, 300);
    const activeKeywords = forwardedKeywords || debouncedSearchText;

    // DIS-1099/DIS-1100: Do NOT pass member_price to search.
    // member_price acts as a FILTER on MFL (returns only loyalty-priced
    // products) AND changes sell_price_cents to the member price, causing
    // price inconsistency between search tile (shows nonMemberPrice.sell_price)
    // and product detail (shows sell_price_cents). Without member_price,
    // both screens show the same sell_price_cents — consistent pricing.
    // Member pricing banners are handled by dedicated carousels (Homepage,
    // Rewards), not inline search results — matching browse/category pattern.
    const searchQuery = useGlobalSearchLines(storeId, activeKeywords);

    // PERF: list-performance-function-references — memoize flatMap
    // DIS-1179: tier-sort applied at render — Member-Only Prices first, then
    // Just-For-You, then remaining; alpha asc within each tier. Always on
    // because this screen has no user sort UI.
    const searchResults = useMemo(
      () => applyTierSort(searchQuery.data?.pages?.flatMap(p => p.data) ?? []),
      [searchQuery.data?.pages],
    );

    // Prepend gift card result if applicable
    const displayResults: (OrderLine | {type: string})[] = useMemo(() => {
      const items: (OrderLine | {type: string})[] = [];
      if (config.FF_IS_GIFT_CARDS_ENABLED && searchText.toLowerCase().includes('gif')) {
        items.push({type: GIFT_CARD_RESULT_TYPE});
      }
      return [...items, ...searchResults];
    }, [searchResults, searchText]);

    // Sync forwarded keywords into local searchText (for the input field
    // display and subsequent debounce cycles).  The query already fires
    // immediately via activeKeywords above — this just syncs the UI.
    useEffect(() => {
      if (forwardedKeywords) {
        setSearchText(forwardedKeywords);
      }
    }, [forwardedKeywords]);

    // Consume forwarded keywords ONLY after debouncedSearchText has caught
    // up to the forwarded value.  Consuming earlier (in the same effect as
    // setSearchText) creates a 300ms dead zone on re-navigation:
    //   forwardedKeywords=undefined + debouncedSearchText='' → activeKeywords=''
    // Waiting for the debounce to settle keeps the synchronous bridge alive
    // until debouncedSearchText can sustain the query on its own.
    useEffect(() => {
      if (forwardedKeywords && debouncedSearchText === forwardedKeywords) {
        navigation.setParams({keywords: undefined});
      }
    }, [forwardedKeywords, debouncedSearchText, navigation]);

    useEffect(() => {
      // we want to clear the search results when we navigate to the search screen
      // but we want to preserve them if we're coming 'back' from a product that we found
      // initially while searching, or any other 'back' button in the app
      if (globalStore.isFromNavigationSearch) {
        setSelectedSearchResult(null);
        setSearchText('');
        setBrand(null);
        setActivations([]);
        setDiscoverArticles([]);
        setScannedBarcodeValue('');
        setIsBarcodeSearching(false);
        setIsBarcode(false);

        globalStore.setIsFromNavigationSearch(false);
        return;
      }

      if (!selectedSearchResult) {
        setBrand(null);
        setActivations([]);
        setDiscoverArticles([]);
        return;
      }
      // Get any matching brands.
      const matchedBrand = brandsStore.brands.filter(
        b => b.brand.toLowerCase() === selectedSearchResult?.brand_name?.toLowerCase(),
      )[0];
      setBrand(matchedBrand);
      let additional = !!matchedBrand;

      // Matching activations
      setActivations(
        promotions.filter(activation =>
          activation.barcodes?.some(bc => {
            if (bc.padStart(14, '0') === selectedSearchResult.product_code) {
              additional = true;
              return true;
            }
          }),
        ),
      );
      // Articles — return the .some() result so .filter() can include matches.
      setDiscoverArticles(
        feedStore.discoverList.filter(article =>
          article.productBarcodes?.some(bc => {
            if (bc.padStart(14, '0') === selectedSearchResult.product_code) {
              additional = true;
              return true;
            }
          }),
        ),
      );

      if (!additional) {
        // React Navigation v7: Navigate to product detail
        // Use ROOT → MAIN → PRODUCT for cross-navigator compatibility
        navigation.navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {
            screen: AppRoute.PRODUCT,
            params: {product: selectedSearchResult},
          },
        });
      }
    }, [
      promotions,
      brandsStore.brands,
      feedStore.discoverList,
      globalStore,
      globalStore.isFromNavigationSearch,
      navigation,
      selectedSearchResult,
    ]);

    useFocusEffect(
      React.useCallback(() => {
        setSelectedSearchResult(null);
        return () => null;
      }, []),
    );

    const handleManualSearchPress = () => {
      if (searchText && searchText.length >= 3) {
        setSelectedSearchResult(null);
        // searchText is already set — the debounce will fire the query.
        // No dispatch needed.
      } else if (scannedBarcodeValue) {
        setSelectedSearchResult(null);
        searchMflByBarcodeValue(scannedBarcodeValue);
      }
    };

    const handleTextChanged = (text: string) => {
      setIsBarcode(false);
      const newTerm = filterSearchText(text);
      if (newTerm !== searchText) {
        if (selectedSearchResult) {
          setSelectedSearchResult(null);
        }
        setScannedBarcodeValue('');
        setSearchText(newTerm);
        // User is manually editing — discard the forwarded keyword bridge
        // so activeKeywords follows the user's input via debouncedSearchText.
        if (forwardedKeywords) {
          navigation.setParams({keywords: undefined});
        }
      }
    };

    const handleTextSubmitEditing = ({nativeEvent}: {nativeEvent: {text: string}}) => {
      const {text} = nativeEvent;
      setIsBarcode(false);
      const newTerm = filterSearchText(text);
      if (selectedSearchResult) {
        setSelectedSearchResult(null);
      }
      setScannedBarcodeValue('');
      setSearchText(newTerm);
    };

    const handleClearSearchPress = () => {
      setIsBarcode(false);
      // Discard forwarded keyword bridge so clearing actually takes effect
      if (forwardedKeywords) {
        navigation.setParams({keywords: undefined});
      }
      handleTextChanged('');
      setScannedBarcodeValue('');
    };

    const searchMflByBarcodeValue = (barcodeValue: string) => {
      // MFL stores product_code as 14-digit GTIN-14 (e.g. "09313419523307").
      // Barcode scanners return EAN-13 (13 digits) or other shorter formats.
      // Pad to GTIN-14 to match the API's expected format.
      const gtin14 = barcodeValue.padStart(14, '0');
      setIsBarcodeSearching(true);
      apiService
        .searchByBarcode([gtin14])
        .then(res => {
          setIsBarcodeSearching(false);
          if (res?.length) {
            setSelectedSearchResult(res[0]);
          } else {
            setSelectedSearchResult(null);
          }
        })
        .catch(err => {
          setIsBarcodeSearching(false);
          logger.error('SearchScreen', 'Error searching MFL by barcode', {
            barcodeValue,
            ...serializeError(err),
          });
        });
    };

    const _handleBarcodeRead = (barcodeValue: string) => {
      setIsBarcode(true);
      setScannedBarcodeValue(barcodeValue);
      setSearchText('');
      setScanning(false);
      searchMflByBarcodeValue(barcodeValue);
    };

    // PERF: list-performance-callbacks — memoize tile press handler
    const handleProductTilePress = useCallback((item: OrderLine) => {
      Keyboard.dismiss();
      setSelectedSearchResult(item);
    }, []);

    // PERF: list-performance-callbacks — memoize renderItem and onEndReached
    const renderItem = useCallback(
      ({item, index}: {item: OrderLine | {type: string}; index: number}) => (
        <Animated.View exiting={SlideOutRight.delay(index * 10).duration(150)}>
          {(item as {type: string}).type === GIFT_CARD_RESULT_TYPE ? (
            <GiftCardTileAbbreviatedHorizontal />
          ) : (
            <ProductTileHorizontal
              item={item}
              pressCallback={handleProductTilePress}
              testContext="search"
            />
          )}
        </Animated.View>
      ),
      [handleProductTilePress],
    );

    const handleEndReached = () => {
      if (searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
        searchQuery.fetchNextPage();
      }
    };

    const renderSearchResults = () => {
      if (selectedSearchResult) return null;

      const isQueryActive = !!activeKeywords && activeKeywords.length >= 3;

      // isLoading = true only on initial fetch with no cached data.
      // isFetching would be true during fetchNextPage too, which killed
      // the list and broke infinite scroll.
      if (searchQuery.isLoading && isQueryActive) {
        return (
          <View style={styles.primaryListWrapper}>
            <View style={[styles.primaryListContent, {gap: 8}]}>
              {Array.from({length: 6}, (_, i) => (
                <SearchItemSkeleton key={i} />
              ))}
            </View>
          </View>
        );
      }

      // Show results only when query is active; otherwise empty (user cleared text).
      const visibleResults = isQueryActive ? displayResults : [];

      return (
        <FlatList
          testID="search-screen-primary-list"
          data={visibleResults}
          renderItem={renderItem}
          keyExtractor={(item, index) =>
            `${(item as OrderLine).id ?? (item as {type: string}).type}-${index}`
          }
          ItemSeparatorComponent={SearchItemSeparator}
          style={styles.primaryListWrapper}
          contentContainerStyle={styles.primaryListContent}
          keyboardShouldPersistTaps="always"
          keyboardDismissMode="on-drag"
          onEndReachedThreshold={0.5}
          onEndReached={handleEndReached}
          ListEmptyComponent={
            !searchQuery.isFetching && isQueryActive && !isBarcode ? (
              <FullScreenMessageView>NO PRODUCTS FOUND</FullScreenMessageView>
            ) : isBarcode && !isBarcodeSearching ? (
              <FullScreenMessageView>Sorry, this product is not available.</FullScreenMessageView>
            ) : null
          }
          ListFooterComponent={
            searchQuery.isFetchingNextPage ? (
              <View style={[FOOTER_STYLE, {gap: 8}]}>
                <SearchItemSkeleton />
                <SearchItemSkeleton />
              </View>
            ) : null
          }
        />
      );
    };

    const renderSelectedSearchResult = () => {
      if (!selectedSearchResult) return null;

      return (
        <Animated.View
          entering={SlideInDown.delay(100).duration(500)}
          exiting={SlideOutDown.duration(100)}
          key={selectedSearchResult.id}>
          <ProductTileHorizontal item={selectedSearchResult} testContext="search" />
        </Animated.View>
      );
    };

    const renderActivationResults = () => {
      if (activations.length === 0) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(300)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Featured in Promotions:</Text>
          {activations.map(activation => (
            <PromotionSearchAbbreviatedTile item={activation} key={activation.id} />
          ))}
        </Animated.View>
      );
    };

    const renderDiscoverResults = () => {
      if (discoverArticles.length === 0) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(600)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Found in Articles:</Text>
          {discoverArticles.map(article => (
            <View key={article.id}>
              <SearchDiscoverAbbreviatedTile item={article} key={article.id} />
              <View style={{marginBottom: 8}} />
            </View>
          ))}
        </Animated.View>
      );
    };

    const renderBrandResults = () => {
      if (!brand) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(500)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Featured Brand:</Text>
          <View>
            <SearchBrandAbbreviatedTile item={brand} />
          </View>
        </Animated.View>
      );
    };

    const renderAdditionalResults = () => (
      <Animated.View
        entering={SlideInDown.duration(500).delay(500)}
        exiting={SlideOutRight.duration(300)}
        style={styles.secondaryListWrapper}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {renderSelectedSearchResult()}
          {renderActivationResults()}
          {renderDiscoverResults()}
          {renderBrandResults()}
        </ScrollView>
      </Animated.View>
    );

    const userInput = searchText || scannedBarcodeValue;
    // TODO: Consider changing this into one section list and changing the data on the fly.
    // That way we can probably reuse the same element and have it neatly apply to the top
    // without having it disappear and re-enter from the bottom of the screen.
    return (
      <ContentView>
        <SearchBar
          value={userInput}
          onManualSearchPress={handleManualSearchPress}
          onTextChanged={handleTextChanged}
          onSubmitEditing={handleTextSubmitEditing}
          onClearSearchPress={handleClearSearchPress}
          onScanBarcodePress={() => setScanning(true)}
          editable={!scanning}
        />
        {renderSearchResults()}
        {renderAdditionalResults()}
        <BarcodeScanner
          visible={scanning}
          onScanned={_handleBarcodeRead}
          onClose={() => setScanning(false)}
        />
      </ContentView>
    );
  },
);

export default SearchScreen;
